import React, { ChangeEvent, useState } from 'react';
import cn from 'classnames';
import './TextareaField.scss';

export type Props = {
  /** Вид поля */
  view?: 'default' | 'clear';

  /** Атрибут value */
  value?: string;

  /** Атрибут placeholder */
  placeholder?: string;

  /** Идентификатор поля */
  id?: string;

  /** Атрибут name */
  name?: string;

  /** Автозаполнение поля */
  autoComplete?: string;

  /** Атрибут maxlength */
  maxLength?: number;

  /** Неактивное состояние поля */
  disabled?: boolean;

  textarea?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;

  /** Обработчик события onFocus */
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  /** Обработчик события onChange */
  onChange?: (event: ChangeEvent) => void;

  /** Обработчик события onBlur */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;

  /** Дополнительный класс */
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export type Ref = HTMLTextAreaElement;

const TextareaField = React.forwardRef<Ref, Props>((props, textareaRef) => {
  const {
    view = 'default',
    value,
    placeholder,
    name,
    autoComplete = 'off',
    maxLength,
    disabled = false,
    id,
    textarea,
    onFocus,
    onChange,
    onBlur,
    className,
    ...attr
  } = props;
  const [focused, setFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    setFocused(true);

    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    setFocused(false);

    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e: ChangeEvent): void => {
    if (onChange) {
      onChange(e);
    }
  };

  const textareaWrapperProps = {
    className: cn(
      'textarea-field',
      view !== 'clear' && `textarea-field_view_${view}`,
      focused && 'textarea-field_focused',
      disabled && 'textarea-field_disabled',
      className
    ),
    ...attr,
  };

  const textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> = {
    ...textarea,
    className: 'textarea-field__textarea',
    id,
    value,
    placeholder,
    name,
    disabled,
    autoComplete,
    maxLength,
    onFocus: handleFocus,
    onChange: handleChange,
    onBlur: handleBlur,
  };

  return (
    <div {...textareaWrapperProps}>
      <textarea ref={textareaRef} {...textareaProps} />
    </div>
  );
});

export default TextareaField;
